import React, { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { TENANT_ID, API_BASE_URL } from '../utils/api';
import { useToast } from '../contexts/ToastContext';
import '../styles/ai-providers.css';

interface AIProvider {
  id: string;
  name: string;
  driver: 'mistral' | 'openrouter' | 'groq' | 'ollama' | 'gemini' | 'openai' | 'cerebras' | 'deepseek' | 'nvidia' | 'jev' | string;
  model: string;
  baseUrl: string | null;
  apiKey: string | null;
  maxRequestsPerDay: number;
  maxTokensPerDay: number;
  priority: number;
  enabled: boolean;
  requestsUsedToday?: number;
  tokensUsedToday?: number;
  classification: 'standard' | 'fast' | 'small' | 'multimodal' | 'audio' | 'system_one';
  supportsImages?: boolean;
  supportsAudio?: boolean;
  supportsSystemOne?: boolean;
  inputCostPerMillion?: number | null;
  outputCostPerMillion?: number | null;
}

interface AIRouterLog {
  id: string;
  providerKey: string;
  model: string | null;
  errorCode: string;
  errorMessage: string;
  payloadInfo: string | null;
  createdAt: string;
}

interface QuickTestResult {
  ok: boolean;
  driver?: string;
  model?: string;
  latencyMs?: number;
  sampleText?: string;
  error?: string;
  testedAt: string;
}

export interface AvailableAIModel {
  id: string;
  name: string;
  description?: string;
  contextLength?: number;
  ownedBy?: string;
  supportsVision: boolean;
  supportsAudio: boolean;
  supportsSystemOne: boolean;
  inputCostPerMillion?: number;
  outputCostPerMillion?: number;
  isFreeTier?: boolean;
}

const emptyProvider: Omit<AIProvider, 'id'> = {
  name: '',
  driver: 'mistral',
  model: '',
  baseUrl: '',
  apiKey: '',
  maxRequestsPerDay: 5000,
  maxTokensPerDay: 2000000,
  priority: 1,
  enabled: true,
  classification: 'standard',
  supportsImages: false,
  supportsAudio: false,
  supportsSystemOne: false,
  inputCostPerMillion: null,
  outputCostPerMillion: null,
};

const driverInfo: Record<string, { icon: string; color: string; label: string }> = {
  mistral: { icon: '🤖', color: '#FF7000', label: 'Mistral AI' },
  openrouter: { icon: '🔀', color: '#6366F1', label: 'OpenRouter' },
  groq: { icon: '⚡', color: '#F97316', label: 'Groq' },
  ollama: { icon: '🦙', color: '#10B981', label: 'Ollama' },
  gemini: { icon: '🤖', color: '#4285F4', label: 'Gemini' },
  openai: { icon: '🤖', color: '#10A37F', label: 'OpenAI' },
  cerebras: { icon: '🧠', color: '#B026FF', label: 'Cerebras' },
  deepseek: { icon: '🐳', color: '#0070F3', label: 'DeepSeek' },
  nvidia: { icon: '🟢', color: '#76B900', label: 'NVIDIA' },
  opencode: { icon: '⚡', color: '#10B981', label: 'OpenCode Go / Zen' },
  jev: { icon: '🎯', color: '#8B5CF6', label: 'Jev (TypeSafe System One)' },
};

const defaultDriverInfo = { icon: '🤖', color: '#6B7280', label: 'Personalizado' };

const getApiKeyPlaceholder = (driver: string): string => {
  switch (driver) {
    case 'openrouter':
      return 'sk-or-v1-...'
    case 'groq':
      return 'gsk_...'
    case 'gemini':
      return 'AIzaSy...'
    case 'mistral':
      return 'mstrl_... / api_key'
    case 'nvidia':
      return 'nvapi-...'
    case 'openai':
      return 'sk-proj-... / sk-...'
    case 'deepseek':
      return 'sk-...'
    case 'cerebras':
      return 'csk-...'
    default:
      return 'sk-... / api-key...'
  }
};

const testPresets = [
  { label: '⚡ Ping rápido', prompt: 'ping' },
  { label: '👋 Saludo y Modelo', prompt: 'Hola, di en una frase concisa que estás operativo y cuál es tu modelo.' },
  { label: '🎯 Clasificación System One (RLCD)', prompt: 'Quiero comprar 2 unidades con delivery para Caracas' },
  { label: '🖼️ Capacidad Visión', prompt: 'Hola, describe en una frase breve tu capacidad para procesar imágenes multimodales.' },
  { label: '🎙️ Transcripción Audio', prompt: 'ping' },
  { label: '📦 JSON Estructurado', prompt: 'Genera un JSON con formato {"status": "ok", "provider": "activo"}' },
  { label: '🛍️ Simulación Ventas', prompt: 'Un cliente pregunta: "¿Tienen delivery para Caracas hoy?". Responde en 2 líneas como asistente amable.' },
];

const AIProvidersPage: React.FC = () => {
  const { token } = useAuth();
  const { addToast } = useToast();
  const [providers, setProviders] = useState<AIProvider[]>([]);
  const [loading, setLoading] = useState(true);

  // Form Modal State
  const [editing, setEditing] = useState<AIProvider | null>(null);
  const [form, setForm] = useState<Omit<AIProvider, 'id'>>(emptyProvider);
  const [showModal, setShowModal] = useState(false);
  const [showFormApiKey, setShowFormApiKey] = useState(false);
  const [modalTesting, setModalTesting] = useState(false);
  const [modalTestResult, setModalTestResult] = useState<QuickTestResult | null>(null);

  // Available Models Explorer State
  const [availableModels, setAvailableModels] = useState<AvailableAIModel[]>([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [fetchingPricing, setFetchingPricing] = useState(false);

  const handleConsultPricing = async (modelOverride?: string, driverOverride?: string) => {
    const modelToQuery = (modelOverride || form.model || '').trim();
    const driverToQuery = driverOverride || form.driver;
    if (!modelToQuery) {
      addToast({ type: 'warning', title: 'Atención', message: 'Ingresa un identificador de modelo para consultar su tarifa' });
      return;
    }
    setFetchingPricing(true);
    try {
      const res = await fetch(`${API_BASE_URL}/admin/ai-providers/query-pricing`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ model: modelToQuery, driver: driverToQuery }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.ok) {
          setForm(prev => ({
            ...prev,
            inputCostPerMillion: data.inputCostPerMillion ?? 0,
            outputCostPerMillion: data.outputCostPerMillion ?? 0,
          }));
          addToast({
            type: 'info',
            title: 'Tarifa consultada',
            message: data.isFreeTier
              ? 'Modelo identificado como Free Tier ($0.00 USD)'
              : `Tarifa catálogo: ${data.inputCostPerMillion} in / ${data.outputCostPerMillion} out / 1M`,
          });
        }
      }
    } catch (err: any) {
      console.warn('Could not query pricing:', err);
    } finally {
      setFetchingPricing(false);
    }
  };

  const [modelSearchQuery, setModelSearchQuery] = useState('');

  // Quick Test Modal State (sin editar)
  const [testingProvider, setTestingProvider] = useState<AIProvider | null>(null);
  const [testPrompt, setTestPrompt] = useState('Hola, confirma en una sola frase breve que tu servicio está operativo.');
  const [testingRunning, setTestingRunning] = useState(false);
  const [quickTestResult, setQuickTestResult] = useState<QuickTestResult | null>(null);

  // Card Key Visibility
  const [revealedKeys, setRevealedKeys] = useState<Record<string, boolean>>({});

  // Filter & Search State
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [filterClassification, setFilterClassification] = useState<'all' | 'fast' | 'standard' | 'small' | 'multimodal' | 'audio' | 'system_one'>('all');
  const [filterVision, setFilterVision] = useState<'all' | 'vision' | 'text'>('all');

  // Logs State
  const [logs, setLogs] = useState<AIRouterLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);

  const headers: HeadersInit = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
    ...(TENANT_ID ? { 'x-tenant-id': TENANT_ID } : {}),
  };

  const loadProviders = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/admin/ai-providers`, { headers });
      if (!res.ok) throw new Error('Failed to load AI providers');
      const data = await res.json();
      setProviders(data);
    } catch (err) {
      console.error(err);
      addToast({ type: 'error', title: 'Error', message: 'No se pudieron cargar los proveedores de IA' });
    } finally {
      setLoading(false);
    }
  };

  const loadLogs = async () => {
    setLogsLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/admin/ai-providers/logs?limit=50`, { headers });
      if (!res.ok) throw new Error('Failed to load AI router logs');
      const data = await res.json();
      setLogs(data);
    } catch (err) {
      console.error(err);
      addToast({ type: 'error', title: 'Error', message: 'No se pudo cargar el historial de fallos' });
    } finally {
      setLogsLoading(false);
    }
  };

  useEffect(() => {
    loadProviders();
    loadLogs();
  }, []);

  // Filtered providers
  const filteredProviders = useMemo(() => {
    return providers.filter(p => {
      const matchesSearch = 
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.model.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.driver.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesStatus = 
        filterStatus === 'all' ? true :
        filterStatus === 'active' ? p.enabled : !p.enabled;

      const matchesClass = 
        filterClassification === 'all' ? true :
        filterClassification === 'audio' ? (p.classification === 'audio' || Boolean(p.supportsAudio)) :
        filterClassification === 'system_one' ? (p.classification === 'system_one' || Boolean(p.supportsSystemOne) || p.driver === 'jev') :
        (p.classification || 'standard') === filterClassification;

      const matchesVision =
        filterVision === 'all' ? true :
        filterVision === 'vision' ? Boolean(p.supportsImages) : !p.supportsImages;

      return matchesSearch && matchesStatus && matchesClass && matchesVision;
    });
  }, [providers, searchQuery, filterStatus, filterClassification, filterVision]);

  // Aggregate Stats
  const stats = useMemo(() => {
    const total = providers.length;
    const active = providers.filter(p => p.enabled).length;
    const vision = providers.filter(p => p.supportsImages).length;
    const audio = providers.filter(p => p.supportsAudio || p.classification === 'audio').length;
    const systemOne = providers.filter(p => p.supportsSystemOne || p.classification === 'system_one' || p.driver === 'jev').length;
    const requestsUsed = providers.reduce((acc, p) => acc + (p.requestsUsedToday || 0), 0);
    const primary = providers.find(p => p.priority === 1 && p.enabled);
    return { total, active, vision, audio, systemOne, requestsUsed, primary };
  }, [providers]);

  const copyToClipboard = async (text: string, label: string = 'Texto copiado') => {
    try {
      await navigator.clipboard.writeText(text);
      addToast({ type: 'success', title: 'Copiado', message: `${label} al portapapeles` });
    } catch (err) {
      console.error(err);
      addToast({ type: 'error', title: 'Error', message: 'No se pudo copiar al portapapeles' });
    }
  };

  const toggleRevealKey = (id: string) => {
    setRevealedKeys(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const startCreate = () => {
    setEditing(null);
    setForm({ ...emptyProvider });
    setShowFormApiKey(false);
    setModalTestResult(null);
    setAvailableModels([]);
    setModelSearchQuery('');
    setLoadingModels(false);
    setShowModal(true);
  };

  const startEdit = (provider: AIProvider) => {
    setEditing(provider);
    setForm({
      name: provider.name,
      driver: provider.driver,
      model: provider.model,
      baseUrl: provider.baseUrl ?? '',
      apiKey: provider.apiKey ?? '',
      maxRequestsPerDay: provider.maxRequestsPerDay,
      maxTokensPerDay: provider.maxTokensPerDay,
      priority: provider.priority,
      enabled: provider.enabled,
      classification: (provider.classification as any) || 'standard',
      supportsImages: Boolean(provider.supportsImages),
      supportsAudio: Boolean(provider.supportsAudio || provider.classification === 'audio'),
      supportsSystemOne: Boolean(provider.supportsSystemOne || provider.classification === 'system_one' || provider.driver === 'jev'),
      inputCostPerMillion: provider.inputCostPerMillion !== undefined ? provider.inputCostPerMillion : null,
      outputCostPerMillion: provider.outputCostPerMillion !== undefined ? provider.outputCostPerMillion : null,
    });
    setShowFormApiKey(false);
    setModalTestResult(null);
    setAvailableModels([]);
    setModelSearchQuery('');
    setLoadingModels(false);
    setShowModal(true);
  };

  const closeForm = () => {
    setEditing(null);
    setForm(emptyProvider);
    setShowModal(false);
    setModalTestResult(null);
    setAvailableModels([]);
    setModelSearchQuery('');
    setLoadingModels(false);
  };

  const isVisionModelHint = (modelName: string): boolean => {
    const lower = modelName.toLowerCase();
    return (
      lower.includes('gpt-4o') ||
      lower.includes('gpt-4-turbo') ||
      lower.includes('gemini') ||
      lower.includes('vision') ||
      lower.includes('qwen') ||
      lower.includes('llava') ||
      lower.includes('pixtral') ||
      lower.includes('claude-3')
    );
  };

  const isAudioModelHint = (modelName: string): boolean => {
    const lower = modelName.toLowerCase();
    return (
      lower.includes('whisper') ||
      lower.includes('audio') ||
      lower.includes('transcription') ||
      lower.includes('speech')
    );
  };

  const isSystemOneModelHint = (modelName: string): boolean => {
    const lower = modelName.toLowerCase();
    return (
      lower.includes('jev') ||
      lower.includes('systemone') ||
      lower.includes('system_one')
    );
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const target = e.target as HTMLInputElement | HTMLSelectElement;
    const { name, value, type } = target;
    const isCheckbox = type === 'checkbox';
    const checked = isCheckbox && 'checked' in target ? (target as HTMLInputElement).checked : undefined;

    if (name === 'inputCostPerMillion' || name === 'outputCostPerMillion') {
      const numVal = value === '' ? null : Number(value);
      setForm(prev => ({ ...prev, [name]: numVal }));
      return;
    }

    if (name === 'driver') {
      setAvailableModels([]);
      setModelSearchQuery('');
    }

    setForm(prev => {
      const next = {
        ...prev,
        [name]: isCheckbox
          ? Boolean(checked)
          : (name === 'maxRequestsPerDay' || name === 'maxTokensPerDay' || name === 'priority')
            ? Number(value)
            : value,
      };

      if (name === 'driver' && (value === 'gemini' || value === 'opencode')) {
        next.supportsImages = true;
        if (next.classification === 'standard') {
          next.classification = 'multimodal';
        }
        if (value === 'opencode' && !next.model) {
          next.model = 'qwen3.8-flash';
        }
        if (value === 'opencode' && !next.baseUrl) {
          next.baseUrl = 'https://opencode.ai/zen/v1';
        }
      } else if (name === 'driver' && value === 'jev') {
        next.supportsSystemOne = true;
        if (next.classification === 'standard') {
          next.classification = 'system_one';
        }
        if (!next.model) {
          next.model = 'jev-1.13-free';
        }
        if (!next.baseUrl) {
          next.baseUrl = 'https://opencode.ai/zen/v1/systemone';
        }
      } else if (name === 'driver' && value === 'groq' && next.classification === 'audio') {
        if (!next.model) {
          next.model = 'whisper-large-v3';
        }
        if (!next.baseUrl) {
          next.baseUrl = 'https://api.groq.com/openai/v1/audio/transcriptions';
        }
      } else if (name === 'model' && isVisionModelHint(String(value))) {
        next.supportsImages = true;
        if (next.classification === 'standard') {
          next.classification = 'multimodal';
        }
      } else if (name === 'model' && isAudioModelHint(String(value))) {
        next.supportsAudio = true;
        if (next.classification === 'standard') {
          next.classification = 'audio';
        }
        if (String(value).toLowerCase().includes('whisper') && next.driver === 'groq' && !next.baseUrl) {
          next.baseUrl = 'https://api.groq.com/openai/v1/audio/transcriptions';
        }
      } else if (name === 'model' && isSystemOneModelHint(String(value))) {
        next.supportsSystemOne = true;
        if (next.classification === 'standard') {
          next.classification = 'system_one';
        }
        if (!next.baseUrl) {
          next.baseUrl = 'https://opencode.ai/zen/v1/systemone';
        }
      } else if (name === 'classification' && value === 'multimodal') {
        next.supportsImages = true;
      } else if (name === 'classification' && value === 'audio') {
        next.supportsAudio = true;
        if (next.driver === 'groq') {
          if (!next.model) next.model = 'whisper-large-v3';
          if (!next.baseUrl) next.baseUrl = 'https://api.groq.com/openai/v1/audio/transcriptions';
        }
      } else if (name === 'classification' && value === 'system_one') {
        next.supportsSystemOne = true;
        if (next.driver === 'mistral' || !next.driver) {
          next.driver = 'jev';
        }
        if (!next.model) {
          next.model = 'jev-1.13-free';
        }
        if (!next.baseUrl) {
          next.baseUrl = 'https://opencode.ai/zen/v1/systemone';
        }
      }

      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const body = {
        name: form.name.trim(),
        driver: form.driver,
        model: form.model.trim(),
        baseUrl: form.baseUrl ? form.baseUrl.trim() : null,
        apiKey: form.apiKey ? form.apiKey.trim() : null,
        maxRequestsPerDay: Number(form.maxRequestsPerDay) || 5000,
        maxTokensPerDay: Number(form.maxTokensPerDay) || 2000000,
        priority: Number(form.priority) || 1,
        enabled: Boolean(form.enabled),
        supportsImages: Boolean(form.supportsImages),
        supportsAudio: Boolean(form.supportsAudio || form.classification === 'audio'),
        supportsSystemOne: Boolean(form.supportsSystemOne || form.classification === 'system_one' || form.driver === 'jev'),
        classification: form.classification || 'standard',
        inputCostPerMillion: form.inputCostPerMillion !== null && form.inputCostPerMillion !== undefined ? Number(form.inputCostPerMillion) : null,
        outputCostPerMillion: form.outputCostPerMillion !== null && form.outputCostPerMillion !== undefined ? Number(form.outputCostPerMillion) : null,
      };
      const method = editing ? 'PUT' : 'POST';
      const url = editing
        ? `${API_BASE_URL}/admin/ai-providers/${editing.id}`
        : `${API_BASE_URL}/admin/ai-providers`;

      const res = await fetch(url, { method, headers, body: JSON.stringify(body) });
      if (!res.ok) {
        const errorData = await res.json().catch(() => null);
        throw new Error(errorData?.error || errorData?.message || `Error del servidor (${res.status})`);
      }
      await loadProviders();
      addToast({ type: 'success', title: 'Guardado', message: 'Proveedor de IA guardado correctamente' });
      closeForm();
    } catch (err: any) {
      console.error(err);
      addToast({ type: 'error', title: 'Error', message: err?.message || 'No se pudo guardar el proveedor de IA' });
    }
  };

  const handleDelete = async (provider: AIProvider) => {
    if (!window.confirm(`¿Seguro que deseas eliminar el proveedor "${provider.name}" (${provider.model})?`)) return;
    try {
      const res = await fetch(`${API_BASE_URL}/admin/ai-providers/${provider.id}`, {
        method: 'DELETE',
        headers,
      });
      if (!res.ok && res.status !== 204) throw new Error('Failed to delete AI provider');
      await loadProviders();
      addToast({ type: 'success', title: 'Eliminado', message: 'Proveedor de IA eliminado' });
    } catch (err) {
      console.error(err);
      addToast({ type: 'error', title: 'Error', message: 'No se pudo eliminar el proveedor de IA' });
    }
  };

  const handleToggleEnabled = async (provider: AIProvider) => {
    try {
      const body = { enabled: !provider.enabled };
      const res = await fetch(`${API_BASE_URL}/admin/ai-providers/${provider.id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Failed to toggle provider state');
      await loadProviders();
      addToast({
        type: 'success',
        title: 'Actualizado',
        message: `Proveedor ${!provider.enabled ? 'activado' : 'desactivado'} correctamente`,
      });
    } catch (err) {
      console.error(err);
      addToast({ type: 'error', title: 'Error', message: 'No se pudo actualizar el estado del proveedor' });
    }
  };

  // Test from inside the Edit/Create Modal
  const handleModalTestConnection = async () => {
    setModalTesting(true);
    setModalTestResult(null);
    try {
      const isSystemOne = Boolean(form.supportsSystemOne || form.classification === 'system_one' || form.driver === 'jev');
      const body = {
        driver: form.driver,
        model: form.model,
        baseUrl: form.baseUrl || null,
        apiKey: form.apiKey || null,
        prompt: isSystemOne ? 'Quiero comprar 2 unidades con delivery para Caracas' : 'ping',
        supportsSystemOne: isSystemOne,
      };

      const res = await fetch(`${API_BASE_URL}/admin/ai-providers/test`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });

      const data = await res.json();
      if (!res.ok || !data.ok) {
        throw new Error(data.error || 'Falló la prueba de conexión');
      }

      setModalTestResult({
        ok: true,
        driver: data.driver,
        model: data.model,
        latencyMs: Math.round(data.latencyMs),
        sampleText: data.sampleText,
        testedAt: new Date().toLocaleTimeString(),
      });

      addToast({
        type: 'success',
        title: 'Conexión OK',
        message: `Respondió en ${Math.round(data.latencyMs)} ms`,
      });
    } catch (err: any) {
      setModalTestResult({
        ok: false,
        error: err?.message || 'Error de conexión',
        testedAt: new Date().toLocaleTimeString(),
      });
      addToast({
        type: 'error',
        title: 'Fallo de prueba',
        message: err?.message || 'Error de conexión con el proveedor',
      });
    } finally {
      setModalTesting(false);
    }
  };

  // Fetch available models from the provider endpoint (/v1/models or official API)
  const handleFetchModels = async () => {
    setLoadingModels(true);
    try {
      const body = {
        driver: form.driver,
        baseUrl: form.baseUrl ? form.baseUrl.trim() : null,
        apiKey: form.apiKey ? form.apiKey.trim() : null,
      };

      const res = await fetch(`${API_BASE_URL}/admin/ai-providers/models`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });

      const data = await res.json();
      if (!res.ok || !data.ok) {
        throw new Error(data.error || 'No se pudieron consultar los modelos');
      }

      const list: AvailableAIModel[] = data.models || [];
      setAvailableModels(list);
      setModelSearchQuery('');
      addToast({
        type: 'success',
        title: 'Modelos disponibles',
        message: `Se encontraron ${list.length} modelos en ${driverInfo[form.driver]?.label || form.driver}`,
      });
    } catch (err: any) {
      console.error(err);
      addToast({
        type: 'error',
        title: 'Error al consultar modelos',
        message: err?.message || 'No se pudieron consultar los modelos del proveedor',
      });
    } finally {
      setLoadingModels(false);
    }
  };

  const handleSelectAvailableModel = (model: AvailableAIModel) => {
    setForm(prev => {
      const next = {
        ...prev,
        model: model.id,
        inputCostPerMillion: model.inputCostPerMillion !== undefined ? model.inputCostPerMillion : prev.inputCostPerMillion,
        outputCostPerMillion: model.outputCostPerMillion !== undefined ? model.outputCostPerMillion : prev.outputCostPerMillion,
      };
      if (model.supportsAudio) {
        next.supportsAudio = true;
        if (next.classification === 'standard') {
          next.classification = 'audio';
        }
      }
      if (model.supportsVision) {
        next.supportsImages = true;
        if (next.classification === 'standard') {
          next.classification = 'multimodal';
        }
      }
      if (model.supportsSystemOne) {
        next.supportsSystemOne = true;
        if (next.classification === 'standard') {
          next.classification = 'system_one';
        }
      }
      return next;
    });
    addToast({
      type: 'info',
      title: 'Modelo seleccionado',
      message: `${model.id}${model.supportsVision ? ' (Visión)' : ''}${model.supportsAudio ? ' (Audio)' : ''}`,
    });
  };

  // Open the Quick Test Modal without editing
  const openQuickTest = (provider: AIProvider) => {
    setTestingProvider(provider);
    setQuickTestResult(null);
    setTestPrompt('Hola, confirma en una sola frase breve que tu servicio está operativo y cuál es tu modelo.');
  };

  const closeQuickTest = () => {
    setTestingProvider(null);
    setQuickTestResult(null);
    setTestingRunning(false);
  };

  // Execute Quick Test
  const handleExecuteQuickTest = async () => {
    if (!testingProvider) return;
    setTestingRunning(true);
    setQuickTestResult(null);
    try {
      const url = `${API_BASE_URL}/admin/ai-providers/${testingProvider.id}/test`;
      const res = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({ prompt: testPrompt }),
      });

      const data = await res.json();
      if (!res.ok || !data.ok) {
        throw new Error(data.error || 'Falló la ejecución de prueba');
      }

      setQuickTestResult({
        ok: true,
        driver: data.driver,
        model: data.model,
        latencyMs: Math.round(data.latencyMs),
        sampleText: data.sampleText,
        testedAt: new Date().toLocaleTimeString(),
      });

      addToast({
        type: 'success',
        title: 'Prueba exitosa',
        message: `Modelo respondió en ${Math.round(data.latencyMs)} ms`,
      });
    } catch (err: any) {
      setQuickTestResult({
        ok: false,
        error: err?.message || 'No se pudo conectar con el LLM',
        testedAt: new Date().toLocaleTimeString(),
      });
      addToast({
        type: 'error',
        title: 'Error de respuesta',
        message: err?.message || 'El modelo no pudo completar la solicitud',
      });
    } finally {
      setTestingRunning(false);
    }
  };

  return (
    <div className="ai-providers-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h2>⚙️ Proveedores de Inteligencia Artificial</h2>
          <p className="page-subtitle">
            Monitoreo y orquestación multimodelo. Prueba respuestas en tiempo real, verifica API keys y gestiona prioridades de fallback sin fricción.
          </p>
        </div>
        <div className="header-actions">
          <button onClick={startCreate} className="btn-primary">
            <span>➕</span> Nuevo Proveedor
          </button>
        </div>
      </div>

      {/* Metrics Banner */}
      <div className="metrics-summary-bar">
        <div className="metric-box">
          <span className="metric-icon">🤖</span>
          <div className="metric-info">
            <span className="metric-value">{stats.total}</span>
            <span className="metric-title">Total Modelos</span>
          </div>
        </div>
        <div className="metric-box">
          <span className="metric-icon">🟢</span>
          <div className="metric-info">
            <span className="metric-value">{stats.active}</span>
            <span className="metric-title">Activos / En Rutas</span>
          </div>
        </div>
        <div className="metric-box">
          <span className="metric-icon">🖼️</span>
          <div className="metric-info">
            <span className="metric-value">{stats.vision}</span>
            <span className="metric-title">Con Visión / Fotos</span>
          </div>
        </div>
        <div className="metric-box">
          <span className="metric-icon">🎙️</span>
          <div className="metric-info">
            <span className="metric-value">{stats.audio}</span>
            <span className="metric-title">Audio / Voz (STT)</span>
          </div>
        </div>
        <div className="metric-box">
          <span className="metric-icon">🎯</span>
          <div className="metric-info">
            <span className="metric-value">{stats.systemOne}</span>
            <span className="metric-title">System One (RLCD)</span>
          </div>
        </div>
        <div className="metric-box">
          <span className="metric-info">📊</span>
          <div className="metric-info">
            <span className="metric-value">{stats.requestsUsed.toLocaleString('es-ES')}</span>
            <span className="metric-title">Peticiones Hoy</span>
          </div>
        </div>
        <div className="metric-box">
          <span className="metric-icon">⭐</span>
          <div className="metric-info">
            <span className="metric-value truncate">{stats.primary ? stats.primary.name : 'Ninguno'}</span>
            <span className="metric-title">Modelo Primario (P1)</span>
          </div>
        </div>
      </div>

      {/* Search & Filter Toolbar */}
      <div className="providers-filter-toolbar">
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Buscar por nombre, modelo o driver..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="clear-search-btn" onClick={() => setSearchQuery('')}>✕</button>
          )}
        </div>

        <div className="filter-chips">
          <div className="chip-group">
            <span className="chip-label">Estado:</span>
            <button 
              className={`filter-chip ${filterStatus === 'all' ? 'active' : ''}`}
              onClick={() => setFilterStatus('all')}
            >
              Todos ({providers.length})
            </button>
            <button 
              className={`filter-chip ${filterStatus === 'active' ? 'active' : ''}`}
              onClick={() => setFilterStatus('active')}
            >
              🟢 Activos ({stats.active})
            </button>
            <button 
              className={`filter-chip ${filterStatus === 'inactive' ? 'active' : ''}`}
              onClick={() => setFilterStatus('inactive')}
            >
              ⚪ Pausados ({stats.total - stats.active})
            </button>
          </div>

          <div className="chip-group">
            <span className="chip-label">Visión:</span>
            <button 
              className={`filter-chip ${filterVision === 'all' ? 'active' : ''}`}
              onClick={() => setFilterVision('all')}
            >
              Todos
            </button>
            <button 
              className={`filter-chip ${filterVision === 'vision' ? 'active' : ''}`}
              onClick={() => setFilterVision('vision')}
            >
              🖼️ Con Visión ({providers.filter(p => p.supportsImages).length})
            </button>
            <button 
              className={`filter-chip ${filterVision === 'text' ? 'active' : ''}`}
              onClick={() => setFilterVision('text')}
            >
              📝 Solo Texto ({providers.filter(p => !p.supportsImages).length})
            </button>
          </div>

          <div className="chip-group">
            <span className="chip-label">Tipo:</span>
            <button 
              className={`filter-chip ${filterClassification === 'all' ? 'active' : ''}`}
              onClick={() => setFilterClassification('all')}
            >
              Todos
            </button>
            <button 
              className={`filter-chip ${filterClassification === 'fast' ? 'active' : ''}`}
              onClick={() => setFilterClassification('fast')}
            >
              ⚡ Rápidos
            </button>
            <button 
              className={`filter-chip ${filterClassification === 'standard' ? 'active' : ''}`}
              onClick={() => setFilterClassification('standard')}
            >
              ⚙️ Estándar
            </button>
            <button 
              className={`filter-chip ${filterClassification === 'multimodal' ? 'active' : ''}`}
              onClick={() => setFilterClassification('multimodal')}
            >
              🖼️ Multimodal
            </button>
            <button 
              className={`filter-chip ${filterClassification === 'audio' ? 'active' : ''}`}
              onClick={() => setFilterClassification('audio')}
            >
              🎙️ Audio ({stats.audio})
            </button>
            <button 
              className={`filter-chip ${filterClassification === 'system_one' ? 'active' : ''}`}
              onClick={() => setFilterClassification('system_one')}
            >
              🎯 System One ({stats.systemOne})
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Cargando catálogo de proveedores...</p>
        </div>
      ) : (
        <>
          {/* Provider Cards Grid */}
          <div className="providers-grid">
            {filteredProviders.map(p => {
              const used = p.requestsUsedToday ?? 0;
              const max = p.maxRequestsPerDay || 0;
              const overLimit = max > 0 && used >= max;
              const percent = max > 0 ? Math.round((used * 100) / max) : 0;
              const info = driverInfo[p.driver] || defaultDriverInfo;
              const isKeyRevealed = Boolean(revealedKeys[p.id]);

              return (
                <div 
                  key={p.id} 
                  className={`provider-card ${!p.enabled ? 'disabled' : ''}`}
                  style={{ borderTop: `4px solid ${info.color}` }}
                >
                  <div className="provider-card-header">
                    <div className="provider-icon" style={{ backgroundColor: `${info.color}20`, color: info.color }}>
                      {info.icon}
                    </div>
                    <div className="provider-info">
                      <div className="title-row">
                        <h3>{p.name}</h3>
                        {p.priority === 1 && (
                          <span className="priority-badge primary">⭐ P1</span>
                        )}
                        {p.priority > 1 && (
                          <span className="priority-badge">P{p.priority}</span>
                        )}
                      </div>
                      <div className="badges-row">
                        <span className="provider-driver">{info.label}</span>
                        <span className={`classification-badge ${p.classification || 'standard'}`}>
                          {p.classification === 'fast' ? '⚡ Rápido' : p.classification === 'small' ? '🔹 Pequeño' : p.classification === 'multimodal' ? '🖼️ Multimodal' : p.classification === 'audio' ? '🎙️ Audio' : p.classification === 'system_one' ? '🎯 System One' : '⚙️ Estándar'}
                        </span>
                        {(p.supportsSystemOne || p.classification === 'system_one' || p.driver === 'jev') && (
                          <span className="systemone-badge active" title="Clasificador estadístico calibrado con RLCD (Choice · Score · Binaria)">
                            🎯 System One (RLCD)
                          </span>
                        )}
                        {(p.supportsAudio || p.classification === 'audio') && (
                          <span className="audio-badge active" title="Este modelo escucha y transcribe notas de voz de WhatsApp">
                            🎙️ Escucha Audio
                          </span>
                        )}
                        {p.supportsImages ? (
                          <span className="vision-badge active" title="Este modelo soporta imágenes/fotos de clientes">
                            🖼️ Visión
                          </span>
                        ) : (!p.supportsAudio && p.classification !== 'audio' && !p.supportsSystemOne && p.classification !== 'system_one' && p.driver !== 'jev') ? (
                          <span className="vision-badge inactive" title="Solo procesa texto">
                            📝 Solo Texto
                          </span>
                        ) : null}
                      </div>
                    </div>

                    <div className="provider-toggle-wrapper">
                      <button 
                        className={`toggle-switch-btn ${p.enabled ? 'active' : 'inactive'}`}
                        onClick={() => handleToggleEnabled(p)}
                        title={p.enabled ? 'Click para pausar' : 'Click para activar'}
                      >
                        <span className="toggle-indicator"></span>
                        <span className="toggle-text">{p.enabled ? 'ON' : 'OFF'}</span>
                      </button>
                    </div>
                  </div>

                  <div className="provider-card-body">
                    {/* Model Info */}
                    <div className="provider-detail">
                      <span className="detail-label">Modelo</span>
                      <span className="detail-value model-name-pill" title={p.model}>{p.model}</span>
                    </div>

                    {/* API Key Inspector on Card */}
                    <div className="provider-detail api-key-detail">
                      <span className="detail-label">API Key</span>
                      <div className="api-key-chip">
                        {p.apiKey ? (
                          <>
                            <span className="key-preview">
                              {isKeyRevealed 
                                ? p.apiKey 
                                : `${p.apiKey.slice(0, 6)}••••••••${p.apiKey.slice(-4)}`
                              }
                            </span>
                            <button
                              type="button"
                              className="key-action-btn"
                              onClick={() => toggleRevealKey(p.id)}
                              title={isKeyRevealed ? "Ocultar clave" : "Ver clave completa"}
                            >
                              {isKeyRevealed ? '🙈' : '👁'}
                            </button>
                            <button
                              type="button"
                              className="key-action-btn"
                              onClick={() => copyToClipboard(p.apiKey || '', 'API Key')}
                              title="Copiar clave"
                            >
                              📋
                            </button>
                          </>
                        ) : (
                          <span className="key-env-badge" title="Toma la clave configurada en backend/.env">
                            ⚙️ Desde .env
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {p.baseUrl && (
                      <div className="provider-detail">
                        <span className="detail-label">Endpoint</span>
                        <span className="detail-value truncate" title={p.baseUrl}>{p.baseUrl}</span>
                      </div>
                    )}

                    
                    <div className="provider-detail">
                      <span className="detail-label">Tarifa de Tokens</span>
                      <span className="detail-value">
                        {p.inputCostPerMillion === 0 && p.outputCostPerMillion === 0 ? (
                          <span className="badge-pill-free">🎉 Free Tier ($0.00)</span>
                        ) : p.inputCostPerMillion != null && p.outputCostPerMillion != null ? (
                          <span className="badge-pill-rate">
                            💵 In: ${p.inputCostPerMillion} / Out: ${p.outputCostPerMillion} / 1M
                          </span>
                        ) : (
                          <span className="badge-pill-auto">⚡ Tarifa estándar de catálogo</span>
                        )}
                      </span>
                    </div>

                    {/* Usage Progress Bar */}
                    <div className="usage-section">
                      <div className="usage-header">
                        <span className="usage-label">Cuota diaria</span>
                        <span className={`usage-percent ${overLimit ? 'over-limit' : ''}`}>
                          {percent}% ({used.toLocaleString()} / {max.toLocaleString()})
                        </span>
                      </div>
                      <div className="progress-bar">
                        <div 
                          className={`progress-fill ${overLimit ? 'over-limit' : ''}`}
                          style={{ width: `${Math.min(percent, 100)}%` }}
                        />
                      </div>
                      {overLimit && (
                        <div className="limit-warning">
                          ⚠️ Límite diario de peticiones alcanzado
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions Footer */}
                  <div className="provider-card-actions">
                    <button 
                      className="btn-quick-test"
                      onClick={() => openQuickTest(p)}
                      title="Probar respuesta del LLM en vivo sin entrar a editar"
                    >
                      <span>⚡</span> Probar Modelo
                    </button>
                    <button 
                      className="btn-secondary btn-sm"
                      onClick={() => startEdit(p)}
                    >
                      ✏️ Editar
                    </button>
                    <button 
                      className="btn-danger-icon"
                      onClick={() => handleDelete(p)}
                      title="Eliminar proveedor"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              );
            })}

            {filteredProviders.length === 0 && (
              <div className="empty-state">
                <div className="empty-icon">🤖</div>
                <h3>No se encontraron proveedores</h3>
                <p>Prueba con otros términos de búsqueda o filtros.</p>
                {searchQuery && (
                  <button onClick={() => setSearchQuery('')} className="btn-secondary">
                    Limpiar Búsqueda
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Quick Test Interactive Modal (Probar LLM sin editar) */}
          {testingProvider && (
            <div className="modal-overlay" onClick={closeQuickTest}>
              <div className="modal-content quick-test-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <div className="test-modal-title">
                    <div 
                      className="provider-icon small" 
                      style={{ 
                        backgroundColor: `${(driverInfo[testingProvider.driver] || defaultDriverInfo).color}20`,
                        color: (driverInfo[testingProvider.driver] || defaultDriverInfo).color 
                      }}
                    >
                      {(driverInfo[testingProvider.driver] || defaultDriverInfo).icon}
                    </div>
                    <div>
                      <h3>Consola de Prueba: {testingProvider.name}</h3>
                      <div className="test-meta">
                        <span className="driver-tag">{(driverInfo[testingProvider.driver] || defaultDriverInfo).label}</span>
                        <span className="model-tag">{testingProvider.model}</span>
                      </div>
                    </div>
                  </div>
                  <button className="modal-close" onClick={closeQuickTest}>✕</button>
                </div>

                <div className="quick-test-body">
                  {/* Preset prompts */}
                  <div className="presets-bar">
                    <span className="presets-title">Plantillas rápidas:</span>
                    <div className="presets-chips">
                      {testPresets.map((preset) => (
                        <button
                          key={preset.label}
                          type="button"
                          className="preset-chip"
                          onClick={() => setTestPrompt(preset.prompt)}
                        >
                          {preset.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Prompt Textarea */}
                  <div className="form-group prompt-group">
                    <label>Prompt a enviar al modelo:</label>
                    <textarea
                      rows={3}
                      className="test-prompt-input"
                      value={testPrompt}
                      onChange={(e) => setTestPrompt(e.target.value)}
                      placeholder="Escribe tu mensaje o instrucción para probar el LLM..."
                    />
                  </div>

                  <div className="test-action-row">
                    <button
                      type="button"
                      className="btn-run-test"
                      onClick={handleExecuteQuickTest}
                      disabled={testingRunning || !testPrompt.trim()}
                    >
                      {testingRunning ? (
                        <>
                          <div className="spinner-small"></div>
                          Consultando LLM...
                        </>
                      ) : (
                        <>🚀 Ejecutar Prueba en Vivo</>
                      )}
                    </button>
                    <span className="test-key-hint">
                      {testingProvider.apiKey 
                        ? '🔑 Usando clave configurada en este proveedor' 
                        : '⚙️ Usando clave del sistema (.env)'}
                    </span>
                  </div>

                  {/* Test Result Box */}
                  {quickTestResult && (
                    <div className={`test-result-box ${quickTestResult.ok ? 'success' : 'error'}`}>
                      <div className="result-header">
                        <div className="result-badge">
                          {quickTestResult.ok ? '✅ Respuesta Exitosa' : '❌ Error de Conexión'}
                        </div>
                        {quickTestResult.latencyMs !== undefined && (
                          <div className={`latency-pill ${quickTestResult.latencyMs < 1000 ? 'fast' : quickTestResult.latencyMs < 3000 ? 'medium' : 'slow'}`}>
                            ⚡ {quickTestResult.latencyMs} ms
                          </div>
                        )}
                        <span className="test-time">{quickTestResult.testedAt}</span>
                      </div>

                      {quickTestResult.ok ? (
                        <div className="result-content">
                          <div className="result-text-preview">
                            <pre>{quickTestResult.sampleText || '(Respuesta vacía recibida del modelo)'}</pre>
                          </div>
                          {quickTestResult.sampleText && (
                            <button
                              type="button"
                              className="copy-response-btn"
                              onClick={() => copyToClipboard(quickTestResult.sampleText || '', 'Respuesta del modelo')}
                            >
                              📋 Copiar Respuesta
                            </button>
                          )}
                        </div>
                      ) : (
                        <div className="result-error-content">
                          <p className="error-title">Detalle del error:</p>
                          <pre className="error-text">{quickTestResult.error}</pre>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="modal-footer">
                  <button type="button" className="btn-secondary" onClick={closeQuickTest}>
                    Cerrar Consola
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Provider Form Modal (Crear / Editar) */}
          {showModal && (
            <div className="modal-overlay" onClick={closeForm}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>{editing ? `✏️ Editar Proveedor: ${editing.name}` : '➕ Nuevo Proveedor de IA'}</h3>
                  <button className="modal-close" onClick={closeForm}>✕</button>
                </div>

                <form onSubmit={handleSubmit} className="provider-form" autoComplete="off">
                  {/* Trampa anti-autofill: neutraliza la heurística del navegador de emparejar usuario y contraseña guardada */}
                  <div 
                    style={{ position: 'absolute', opacity: 0, height: 0, width: 0, zIndex: -1, overflow: 'hidden' }} 
                    aria-hidden="true"
                  >
                    <input type="text" name="prevent_autofill_username" tabIndex={-1} autoComplete="off" />
                    <input type="password" name="prevent_autofill_password" tabIndex={-1} autoComplete="new-password" />
                  </div>
                  <div className="form-section">
                    <h4>Información Básica</h4>
                    
                    <div className="form-group">
                      <label>Nombre del Proveedor</label>
                      <input 
                        name="name" 
                        value={form.name} 
                        onChange={handleChange} 
                        placeholder="Ej: Mistral Principal, NVIDIA Ultra, Groq Fast"
                        required 
                      />
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Driver / Motor</label>
                        <select name="driver" value={form.driver} onChange={handleChange}>
                          <option value="mistral">🤖 Mistral AI</option>
                          <option value="opencode">⚡ OpenCode Go / Zen</option>
                          <option value="nvidia">🟢 NVIDIA</option>
                          <option value="groq">⚡ Groq</option>
                          <option value="openai">🤖 OpenAI</option>
                          <option value="gemini">✨ Gemini</option>
                          <option value="deepseek">🐳 DeepSeek</option>
                          <option value="openrouter">🔀 OpenRouter</option>
                          <option value="cerebras">🧠 Cerebras</option>
                          <option value="ollama">🦙 Ollama</option>
                          <option value="jev">🎯 Jev (TypeSafe System One)</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label>Prioridad</label>
                        <input 
                          type="number" 
                          name="priority" 
                          value={form.priority} 
                          onChange={handleChange} 
                          min={1}
                          placeholder="1 = más alta prioridad"
                        />
                      </div>

                      <div className="form-group">
                        <label>Clasificación de Rol</label>
                        <select name="classification" value={form.classification} onChange={handleChange}>
                          <option value="standard">⚙️ Estándar (General / Ventas)</option>
                          <option value="fast">⚡ Rápido (Enrutamiento / Clasificación)</option>
                          <option value="small">🔹 Pequeño (Tareas Ultralivianas)</option>
                          <option value="multimodal">🖼️ Multimodal (Visión + Texto)</option>
                          <option value="audio">🎙️ Audio / Voz (Whisper STT - Transcripción WhatsApp)</option>
                          <option value="system_one">🎯 System One (RLCD: Choice / Score / Binaria)</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="form-section">
                    <h4>Configuración de Conexión & Credenciales</h4>
                    
                    <div className="form-group">
                      <div className="model-label-row">
                        <label>Identificador del Modelo</label>
                        <button
                          type="button"
                          className="btn-fetch-models"
                          onClick={handleFetchModels}
                          disabled={loadingModels}
                          title="Consultar modelos disponibles en el endpoint /v1/models o API oficial del proveedor"
                        >
                          {loadingModels ? (
                            <>
                              <span className="spinner-small" /> Consultando...
                            </>
                          ) : (
                            <>
                              ⚡ Listar Modelos Disponibles
                            </>
                          )}
                        </button>
                      </div>

                      <div className="model-input-wrapper">
                        <input 
                          name="model" 
                          value={form.model} 
                          onChange={handleChange} 
                          placeholder="Ej: mistral-small-latest, nvidia/nemotron-3-super-120b-a12b, gpt-4o-mini"
                          required 
                          list="available-models-datalist"
                        />
                        <datalist id="available-models-datalist">
                          {availableModels.map((m) => (
                            <option key={m.id} value={m.id}>
                              {m.name || m.id} {m.supportsVision ? '(Visión)' : ''} {m.supportsAudio ? '(Audio)' : ''} {m.supportsSystemOne ? '(System One)' : ''}
                            </option>
                          ))}
                        </datalist>
                      </div>
                      <small className="form-hint">
                        Ingresa el identificador manualmente o haz clic en "Listar Modelos Disponibles" para seleccionar de la lista oficial del proveedor.
                      </small>

                      {/* Dropdown Explorer cuando hay modelos disponibles */}
                      {availableModels.length > 0 && (
                        <div className="models-explorer-card">
                          <div className="models-explorer-header">
                            <span className="models-explorer-title">
                              📋 <strong>{availableModels.length}</strong> modelos detectados en {driverInfo[form.driver]?.label || form.driver}:
                            </span>
                            {availableModels.length > 6 && (
                              <input
                                type="text"
                                className="models-search-input"
                                placeholder="Filtrar modelos (ej. whisper, llama, flash)..."
                                value={modelSearchQuery}
                                onChange={(e) => setModelSearchQuery(e.target.value)}
                              />
                            )}
                          </div>

                          <div className="models-select-container">
                            <select
                              className="models-dropdown"
                              value={form.model}
                              onChange={(e) => {
                                const found = availableModels.find((m) => m.id === e.target.value);
                                if (found) handleSelectAvailableModel(found);
                              }}
                            >
                              <option value="">-- Selecciona un modelo para autorrellenar --</option>
                              {availableModels
                                .filter((m) =>
                                  !modelSearchQuery ||
                                  m.id.toLowerCase().includes(modelSearchQuery.toLowerCase()) ||
                                  m.name.toLowerCase().includes(modelSearchQuery.toLowerCase())
                                )
                                .map((m) => (
                                  <option key={m.id} value={m.id}>
                                    {m.id} {m.name && m.name !== m.id ? `(${m.name})` : ''} {m.isFreeTier ? ' • [Free Tier]' : (m.inputCostPerMillion !== undefined ? ` • [${m.inputCostPerMillion}/${m.outputCostPerMillion}]` : '')}
                                    {m.supportsVision ? ' 🖼️ Visión' : ''}
                                    {m.supportsAudio ? ' 🎙️ Audio' : ''}
                                    {m.supportsSystemOne ? ' 🎯 System One' : ''}
                                    {m.contextLength ? ` [${Math.round(m.contextLength / 1000)}k ctx]` : ''}
                                  </option>
                                ))}
                            </select>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="form-group">
                      <label>URL Base del Endpoint (Opcional)</label>
                      <input 
                        type="text"
                        name="baseUrl" 
                        value={form.baseUrl ?? ''} 
                        onChange={handleChange} 
                        placeholder="Ej: https://integrate.api.nvidia.com/v1"
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck={false}
                        data-lpignore="true"
                        data-1p-ignore="true"
                        data-bwignore="true"
                      />
                      <small className="form-hint">Déjalo vacío para usar el endpoint oficial predeterminado del proveedor.</small>
                    </div>

                    {/* API Key with Show/Hide and Copy */}
                    <div className="form-group">
                      <div className="label-with-actions">
                        <label>API Key</label>
                        <div className="label-buttons">
                          <button
                            type="button"
                            className="btn-text-action"
                            onClick={() => setShowFormApiKey(!showFormApiKey)}
                          >
                            {showFormApiKey ? '🙈 Ocultar Clave' : '👁 Ver Clave'}
                          </button>
                          {form.apiKey && (
                            <button
                              type="button"
                              className="btn-text-action"
                              onClick={() => copyToClipboard(form.apiKey || '', 'API Key')}
                            >
                              📋 Copiar
                            </button>
                          )}
                        </div>
                      </div>
                      
                      <div className="input-with-button-wrapper">
                        <input 
                          type={showFormApiKey ? "text" : "password"}
                          name="apiKey" 
                          value={form.apiKey ?? ''} 
                          onChange={handleChange}
                          placeholder={getApiKeyPlaceholder(form.driver)}
                          className="api-key-input"
                          autoComplete="new-password"
                          autoCorrect="off"
                          autoCapitalize="off"
                          spellCheck={false}
                          data-lpignore="true"
                          data-1p-ignore="true"
                          data-bwignore="true"
                          data-form-type="other"
                        />
                      </div>
                      <small className="form-hint">
                        💡 <strong>Nota de Seguridad:</strong> Si dejas la clave vacía, el servidor usará la clave definida en <code>backend/.env</code> correspondiente a este driver.
                      </small>
                    </div>

                    {/* Inline Connection Test inside Modal */}
                    <div className="modal-test-strip">
                      <button
                        type="button"
                        className="btn-test"
                        onClick={handleModalTestConnection}
                        disabled={modalTesting || !form.model}
                      >
                        {modalTesting ? (
                          <>
                            <div className="spinner-small"></div>
                            Probando conexión en vivo...
                          </>
                        ) : (
                          <>🔌 Probar Conexión Ahora</>
                        )}
                      </button>

                      {modalTestResult && (
                        <div className={`modal-test-feedback ${modalTestResult.ok ? 'success' : 'error'}`}>
                          {modalTestResult.ok ? (
                            <span>
                              ✅ <strong>Conectado:</strong> {modalTestResult.latencyMs} ms · <em>"{modalTestResult.sampleText?.slice(0, 60)}..."</em>
                            </span>
                          ) : (
                            <span>
                              ❌ <strong>Error:</strong> {modalTestResult.error}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="form-section">
                    <h4>Cuotas y Límites Diarios</h4>
                    
                    <div className="form-row">
                      <div className="form-group">
                        <label>Máx. Requests / Día</label>
                        <input 
                          type="number" 
                          name="maxRequestsPerDay" 
                          value={form.maxRequestsPerDay} 
                          onChange={handleChange} 
                          min={1}
                        />
                      </div>

                      <div className="form-group">
                        <label>Máx. Tokens / Día</label>
                        <input 
                          type="number" 
                          name="maxTokensPerDay" 
                          value={form.maxTokensPerDay} 
                          onChange={handleChange} 
                          min={1}
                        />
                      </div>
                    </div>
                  </div>

                  
                  <div className="form-section pricing-form-section">
                    <div className="section-title-with-action">
                      <h4>💰 Estructura de Costos de Tokens ($ USD / 1M tokens)</h4>
                      <button
                        type="button"
                        className="btn-fetch-pricing"
                        onClick={() => handleConsultPricing()}
                        disabled={fetchingPricing || !form.model}
                        title="Consultar tarifas oficiales del catálogo para este modelo"
                      >
                        {fetchingPricing ? 'Consultando...' : '⚡ Consultar Tarifa Oficial'}
                      </button>
                    </div>
                    <p className="pricing-section-desc">
                      Tarifas por millón (1M) de tokens de entrada (prompt) y salida (completion) tomadas en cuenta para calcular el consumo exacto en dólares de cada tenant en <code>admin/ai-usage</code>.
                    </p>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Tarifa Entrada (Prompt) / 1M tokens</label>
                        <div className="input-currency-wrapper">
                          <span className="currency-symbol">$</span>
                          <input
                            type="number"
                            step="0.0001"
                            min="0"
                            name="inputCostPerMillion"
                            value={form.inputCostPerMillion !== null && form.inputCostPerMillion !== undefined ? form.inputCostPerMillion : ''}
                            onChange={handleChange}
                            placeholder="Ej: 0.59 (o 0 para Free Tier)"
                          />
                        </div>
                        <small className="form-hint">Costo en USD por cada 1,000,000 tokens de entrada</small>
                      </div>

                      <div className="form-group">
                        <label>Tarifa Salida (Completion) / 1M tokens</label>
                        <div className="input-currency-wrapper">
                          <span className="currency-symbol">$</span>
                          <input
                            type="number"
                            step="0.0001"
                            min="0"
                            name="outputCostPerMillion"
                            value={form.outputCostPerMillion !== null && form.outputCostPerMillion !== undefined ? form.outputCostPerMillion : ''}
                            onChange={handleChange}
                            placeholder="Ej: 0.79 (o 0 para Free Tier)"
                          />
                        </div>
                        <small className="form-hint">Costo en USD por cada 1,000,000 tokens de salida</small>
                      </div>
                    </div>

                    {form.inputCostPerMillion !== null && form.outputCostPerMillion !== null && (
                      <div className="pricing-sample-pill">
                        {Number(form.inputCostPerMillion) === 0 && Number(form.outputCostPerMillion) === 0 ? (
                          <span className="text-free-pill">
                            🎉 Modelo configurado como Free Tier ($0.00 USD de consumo por llamada)
                          </span>
                        ) : (
                          <span>
                            📊 <strong>Llamada típica</strong> (1,000 prompt + 500 completion):{' '}
                            <code className="sample-calc-val">
                              ${Number((((1000 * (Number(form.inputCostPerMillion) || 0)) + (500 * (Number(form.outputCostPerMillion) || 0))) / 1000000).toFixed(6))} USD
                            </code>
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="form-section vision-form-section">
                    <h4>🖼️ Capacidades de Visión Multimodal</h4>
                    <label className="checkbox-label vision-checkbox-label">
                      <input 
                        type="checkbox" 
                        name="supportsImages" 
                        checked={Boolean(form.supportsImages)} 
                        onChange={handleChange} 
                      />
                      <div className="vision-toggle-content">
                        <span className="vision-toggle-title">
                          Soporta Entrada y Análisis de Imágenes (Vision / VLM)
                        </span>
                        <p className="vision-toggle-desc">
                          Activa esta opción si el modelo acepta imágenes para clasificar productos o buscar en catálogo a partir de fotos enviadas por los clientes en WhatsApp.
                        </p>
                      </div>
                    </label>
                  </div>

                  <div className="form-section audio-form-section">
                    <h4>🎙️ Capacidades de Audio y Notas de Voz</h4>
                    <label className="checkbox-label audio-checkbox-label">
                      <input 
                        type="checkbox" 
                        name="supportsAudio" 
                        checked={Boolean(form.supportsAudio)} 
                        onChange={handleChange} 
                      />
                      <div className="audio-toggle-content">
                        <span className="audio-toggle-title">
                          Soporta Transcripción de Audio / Voz (Speech-to-Text / Whisper)
                        </span>
                        <p className="audio-toggle-desc">
                          Activa esta opción para que el bot escuche y transcriba automáticamente las notas de voz recibidas por WhatsApp con este modelo (ej. Whisper Large v3 en Groq: <code>https://api.groq.com/openai/v1/audio/transcriptions</code>).
                        </p>
                      </div>
                    </label>
                  </div>

                  <div className="form-section systemone-form-section">
                    <h4>🎯 Capacidades System One (TypeSafe / RLCD)</h4>
                    <label className="checkbox-label systemone-checkbox-label">
                      <input 
                        type="checkbox" 
                        name="supportsSystemOne" 
                        checked={Boolean(form.supportsSystemOne || form.classification === 'system_one' || form.driver === 'jev')} 
                        onChange={handleChange} 
                      />
                      <div className="systemone-toggle-content">
                        <span className="systemone-toggle-title">
                          Habilitar como Clasificador Estadístico System One
                        </span>
                        <p className="systemone-toggle-desc">
                          Permite clasificaciones deterministas de ultra-baja latencia con calibración estadística RLCD (preguntas de tipo <em>Choice</em>, <em>Score</em> y <em>Binaria/Noul</em>) a través de TypeSafe AI / Jev sin alucinaciones de texto libre.
                        </p>
                      </div>
                    </label>
                  </div>

                  <div className="form-section">
                    <label className="checkbox-label">
                      <input 
                        type="checkbox" 
                        name="enabled" 
                        checked={form.enabled} 
                        onChange={handleChange} 
                      />
                      <span>Habilitar este proveedor inmediatamente para tráfico del bot</span>
                    </label>
                  </div>

                  <div className="form-actions">
                    <button 
                      type="button" 
                      className="btn-secondary"
                      onClick={closeForm}
                    >
                      Cancelar
                    </button>
                    <button type="submit" className="btn-primary">
                      {editing ? '💾 Guardar Cambios' : '➕ Crear Proveedor'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Error Logs Section */}
          <div className="logs-section">
            <div className="section-header">
              <div>
                <h3>📋 Registro de Fallos y Fallbacks</h3>
                <p className="logs-subtitle">Eventos de degradación o errores capturados por el AI Router para diagnóstico.</p>
              </div>
              <button onClick={loadLogs} className="btn-secondary btn-sm" disabled={logsLoading}>
                {logsLoading ? (
                  <>
                    <div className="spinner-small"></div>
                    Actualizando...
                  </>
                ) : (
                  <>🔄 Refrescar Logs</>
                )}
              </button>
            </div>

            {logsLoading && logs.length === 0 ? (
              <div className="loading-container">
                <div className="spinner"></div>
                <p>Cargando historial...</p>
              </div>
            ) : logs.length === 0 ? (
              <div className="empty-logs">
                <span className="empty-icon">✅</span>
                <p>Sin fallos registrados recientemente. El router está operando de manera óptima.</p>
              </div>
            ) : (
              <div className="logs-list">
                {logs.map(log => {
                  const date = new Date(log.createdAt);
                  return (
                    <div key={log.id} className="log-item">
                      <div className="log-header">
                        <span className="log-provider">{log.providerKey}</span>
                        <span className="log-time">{date.toLocaleString('es-ES')}</span>
                      </div>
                      <div className="log-body">
                        <span className="log-code">{log.errorCode}</span>
                        {log.model && <span className="log-model">· {log.model}</span>}
                      </div>
                      <div className="log-message">{log.errorMessage}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AIProvidersPage;
